import React, { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import animation from './animation.module.scss'
import { usePathname } from 'next/navigation'

type OpenFrom = 'left' | 'right'

interface Props {
    children: React.ReactNode
    className?: string
    isOpen: boolean
    /** Habilita a barra de rolagem. */
    showScrollbar?: boolean
    /** Direção em que o drawer irá abrir. */
    openFrom?: OpenFrom
    onClose: () => void
}

export default function Drawer(props: Props) {
    const animationDuration = 0.2
    const [animationClass, setAnimationClass] = useState(animation.openFromLeft)
    const [bgAnimationClass, setBgAnimationClass] = useState(animation.showBackground)
    const [showScrollBar, setShowScrollBar] = useState<boolean>(false)
    const [isVisible, setIsVisible] = useState(false)
    const pathname = usePathname()
    const previousPathnameRef = useRef<string | null>(null)

    const overlay = useRef(null)
    const wrapper = useRef<HTMLDivElement | null>(null)

    const onDismiss = useCallback(async () => {
        handleCloseAnimation()
        setIsVisible(false)
        setTimeout(() => {
            props.onClose()
        }, animationDuration * 1000)
    }, [])

    const onClick: MouseEventHandler = useCallback(
        (e) => {
            if (e.target === overlay.current) {
                if (onDismiss) onDismiss()
            }
        },
        [onDismiss, overlay]
    )

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape")
                onDismiss()
        },
        [onDismiss]
    )

    const handleOpenAnimation = () => {
        setBgAnimationClass(animation.showBackground)
        setIsVisible(true)
        if (props.openFrom === 'right')
            setAnimationClass(animation.openFromRight)
        else
            setAnimationClass(animation.openFromLeft)
    }

    const handleCloseAnimation = () => {
        setBgAnimationClass(animation.hideBackground)
        if (props.openFrom === 'right')
            setAnimationClass(animation.hideFromRight)
        else
            setAnimationClass(animation.hideFromLeft)
    }

    useEffect(() => {
        if (previousPathnameRef.current && previousPathnameRef.current !== pathname)
            onDismiss()
        previousPathnameRef.current = pathname
    }, [pathname, onDismiss])

    useEffect(() => {
        if (props.showScrollbar)
            setShowScrollBar(props.showScrollbar)
        else
            setShowScrollBar(window.innerWidth > window.innerHeight)
    }, [props.showScrollbar])

    useEffect(() => {
        handleOpenAnimation()

        document?.addEventListener("keydown", onKeyDown)
        return () => document?.removeEventListener("keydown", onKeyDown)
    }, [onKeyDown, props.isOpen])

    useEffect(() => {
        if (isVisible && wrapper.current) {
            const element = wrapper.current

            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'data-dismiss' &&
                        element.getAttribute('data-dismiss') === 'true'
                    ) {
                        onDismiss()
                    }
                })
            })

            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-dismiss'],
            })

            return () => observer.disconnect()
        }
    }, [isVisible, onDismiss])

    return (
        <div ref={overlay} className={`${styles.background} ${bgAnimationClass}`} onClick={onClick}>
            <div
                ref={wrapper}
                className={`${styles.container} ${!showScrollBar && styles.hideScrollbar} ${props.className ?? ""} ${animationClass} generic-modal`}
                data-dismiss='false'
            >
                {props.children}
            </div>
        </div>
    )
}